import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, FileSpreadsheet, ShoppingCart, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };
const pad = (n) => String(n).padStart(2, '0');
const rangeFromMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { date_from: `${y}-${pad(m)}-01`, date_to: `${y}-${pad(m)}-${pad(last)}` };
};
const monthDefault = () => {
  const d = new Date();
  return rangeFromMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
};

export default function AccountingPurchaseIvaBook() {
  useOutletContext();
  const [range, setRange] = useState(monthDefault());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const r = await axios.get(`${BACKEND_URL}/api/accounting/iva-purchase-book`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Libro IVA Compras'); } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const buildUrl = (ext) => {
    const url = new URL(`${BACKEND_URL}/api/accounting/iva-purchase-book.${ext}`);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  return (
    <div className="space-y-6" data-testid="iva-purchase-book-page">
      <div className="flex items-center gap-3">
        <ShoppingCart className="w-7 h-7 text-amber-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Libro IVA Compras</h1>
          <p className="text-sm text-muted-foreground">Agrupado por <strong>fecha de registración</strong> (los comprobantes se imputan al período en que se cargan a la plataforma, no al de su emisión).</p>
        </div>
        <Link to="/accounting/closures">
          <Button variant="outline" size="sm" data-testid="btn-manage-closures">
            <Lock className="w-4 h-4 mr-2" />Cierres contables
          </Button>
        </Link>
      </div>

      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs">Período (mes calendario)</Label>
            <Input
              type="month"
              value={String(range.date_from || '').slice(0, 7)}
              onChange={e => e.target.value && setRange(rangeFromMonth(e.target.value))}
              data-testid="purchase-period-month"
            />
          </div>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => window.open(buildUrl('pdf'), '_blank')} data-testid="purchase-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={() => window.open(buildUrl('xlsx'), '_blank')} data-testid="purchase-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          </div>
        </div>
      </CardContent></Card>

      {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : data && (
        <Card><CardContent className="pt-6">
          {data.rows.length === 0 && (
            <p className="text-xs text-muted-foreground mb-3" data-testid="purchase-empty-hint">
              Todavía no hay comprobantes de compra cargados. La captura fiscal desde Recepciones y Rendición de Anticipos se habilita en la próxima entrega.
            </p>
          )}
          <Table data-testid="purchase-table">
            <TableHeader><TableRow>
              <TableHead className="w-24">Registrado</TableHead>
              <TableHead className="w-24">F. Comprob.</TableHead><TableHead>Comprobante</TableHead>
              <TableHead>CUIT</TableHead><TableHead>Razón Social</TableHead><TableHead>Cond. IVA</TableHead>
              <TableHead className="text-right">Neto Gravado</TableHead><TableHead className="text-right">IVA</TableHead>
              <TableHead className="text-right">Exento</TableHead><TableHead className="text-right">Percepciones</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r) => {
                const lateBooking = r.booking_date && r.date && String(r.booking_date).slice(0, 7) !== String(r.date).slice(0, 7);
                return (
                <TableRow key={r.id} className={lateBooking ? 'bg-amber-50/40' : ''}>
                  <TableCell className="text-xs font-medium">{fmtDate(r.booking_date || r.date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(r.date)}</TableCell>
                  <TableCell className="font-medium">{r.comprobante}</TableCell>
                  <TableCell className="font-mono text-xs">{r.tax_id}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell className="text-xs">{r.iva_condition}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.neto_gravado)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.iva)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.exento)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.percepciones)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtMoney(r.total)}</TableCell>
                </TableRow>
              );})}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-6 text-muted-foreground">Sin comprobantes en el período</TableCell></TableRow>}
              <TableRow className="bg-muted font-bold border-t-2">
                <TableCell colSpan={6}>TOTALES</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.neto_gravado)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.iva)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.exento)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.percepciones)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}
